import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {combineLatest, defer, from, mergeMap, Observable, throwError} from 'rxjs';
import { ReceiptDetails } from '../models/ReceiptDetails';
import { Giftcard, Order } from '../models/Order';
import { BookingResponse } from '../models/Booking';
import {catchError, flatMap, map, tap} from 'rxjs/operators';
import { ConfigService } from './config.service';
import { User } from 'firebase/auth';
import * as packageJson from '../../../package.json';
import { loadDiagQuery } from '../utils/load-diag';

const { name, version } = packageJson;

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private http: HttpClient,
    private cfg: ConfigService
  ) { }

  private getOptions(venueId: string): any {
    const APP_VERSION = `${name}-${version}`;
    const headers = new HttpHeaders({ "Venue-Id": venueId, "Guest-Client-Version": APP_VERSION });
    return { headers, observe: "body", responseType: 'json' };
  }

  private postWithUserToken(venueId: string, user: User, body: any): Observable<Order> {
    // Get user token, then make sure we have a config in place, then make the request.
    // defer: every (re)subscription, e.g. a retry in AuthService, gets the token again instead of reusing a stale one
    const post = (forceRefresh: boolean) => defer(() => from(user.getIdToken(forceRefresh))).pipe(
      mergeMap(token => {
        return this.cfg.getNonEmptyConfigInit(venueId).pipe(
          mergeMap(cfg => {
            const o = this.getOptions(venueId);
            o.headers = o.headers.append("Authorization", token);
            // Only the sign-in goes this way; the load diagnostics ride along in the query string
            return this.http.post<Order>(`${this.cfg.base_url}/api/mnu/exp?${loadDiagQuery()}`, body, o) as unknown as Observable<Order>;
          })
        );
      }),
    );
    // 401 = the server rejected the id token as expired/invalid. The SDK hands out its cached token as long as it
    // looks valid by the device clock, so force a fresh one from Firebase and try once more.
    return post(false).pipe(
      catchError(error => error?.status === 401 ? post(true) : throwError(() => error))
    );
  }

  private postWithOptions<T>(venueId: string, url: string, body: object): Observable<T> {
    const o: any = this.getOptions(venueId);
    return this.http.post<T>(url, body, o) as unknown as Observable<T>;
  }

  public getCustomTokenForMessenger(magicLink: string): Observable<string> {
    const url = `${this.cfg.base_url}/api/mnu/exp`;
    const body = { action: "getcustomtoken", ml: magicLink };
    const options: any = { headers: new HttpHeaders({ "Content-Type": "application/json" }), responseType: 'text' };
    return this.http.post<string>(url, body, options) as unknown as Observable<string>;
  }

  public getCustomTokenForUrlToken(urlSec: string, uid: string): Observable<string> {
    const body = { action: "getcustomurltoken", url_sec: urlSec, uid };
    const url = `${this.cfg.base_url}/api/mnu/exp`;
    const options: any = { headers: new HttpHeaders({ "Content-Type": "application/json" }), responseType: 'text' };
    return this.http.post<string>(url, body, options) as unknown as Observable<string>;
  }

  // public resolveChat(user: firebase.User, table: string, venueId: string, deliveryType?: string): Observable<Order> {
  //   const body = { action: "resolvechat", table, user, deliveryType };
  //   return this.postWithUserToken(venueId, user, body);
  // }

  public signInFromFirebase(user: User, tableCode: string | null, venueId: string, oldFirebaseUser: User | null): Observable<Order> {
    const body = { action: "sign_in_from_firebase", table_code: tableCode, user, old_user: oldFirebaseUser };
    return this.postWithUserToken(venueId, user, body);
  }

  public getReceiptDetails(venueId: string, receiptId: string, includeDossier: boolean): Observable<ReceiptDetails> {
    const body = { action: "receipt_details", receipt_id: receiptId, include_dossier: includeDossier };
    return this.postWithOptions<ReceiptDetails>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public sendReceiptPdf(venueId: string, receiptKey: string, email: string, saveEmail: boolean): Observable<void> {
    const body = { action: "send_receipt_pdf", receipt_key: receiptKey, save: saveEmail, email };
    return this.postWithOptions<void>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public sendVoucherPdf(venueId: string, voucherId: string, email: string): Observable<void> {
    const body = { action: "send_voucher_pdf", voucher_id: voucherId, email };
    return this.postWithOptions<void>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public sendAllVoucherPdfs(venueId: string, receiptKey: string, email: string): Observable<void> {
    const body = { action: "send_all_voucher_pdfs", receipt_key: receiptKey, email };
    return this.postWithOptions<void>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

  public sendGiftcard(venueId: string, postId: string, email: string): Observable<void> {
    const body = { action: "send_giftcard", post_id: postId , email };
    return this.postWithOptions<void>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

  // public fetchInitialOrder(venueId: string, token: string, urlTableParam: string | undefined): Observable<Order> {
  //   // @ts-ignore
  //   const headers = new HttpHeaders({Authorization: token, "Venue-Id": venueId});
  //   const options = {headers, observe: "body", responseType: 'json'};
  //   const url = `${this.cfg.base_url}/api/mnu/exp`;
  //   const body = {action: "order", table: urlTableParam};
  //   // @ts-ignore
  //   return this.http.post<Order>(url, body, options) as unknown as Observable<Order>;
  // }

  public getBookingInfo(venueId: string, bookingId: string): Observable<BookingResponse> {
    const body = { action: "booking_info", venue_id: venueId, booking_id: bookingId };
    return this.postWithOptions<BookingResponse>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }


  public getGiftcard(venueId: string, postId: string): Observable<Giftcard> {
    const body = { action: "giftcard", post_id: postId };
    return this.postWithOptions<Giftcard>(venueId, `${this.cfg.base_url}/api/mnu/exp`, body);
  }

}
